import React, { useState } from 'react';
import { Trash2, ArrowDown } from 'lucide-react';
import { Modal, Button, Pill } from '../../index';

/**
 * DeleteScoringOverrideModal — confirm removal of a scoring config
 * override and show what the agent / tenant will fall back to.
 *
 * Deleting an override means the scope inherits from the next-most-
 * specific scope (AGENT → TENANT → ORG → platform default). The
 * preview makes the consequence concrete: "if you delete this, the
 * effective threshold becomes 95 (currently 90)".
 *
 * Phase A3 / G303. Wires to deleteTrustScoringConfig().
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {'ORG'|'TENANT'|'AGENT'} props.scope
 * @param {Object} props.current        — the config being deleted
 * @param {Object} [props.fallback]     — the config it will inherit from
 * @param {Function} props.onSubmit
 */
export function DeleteScoringOverrideModal({ isOpen, onClose, scope, current, fallback, onSubmit }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!current) return null;

  const fallbackScope = scope === 'AGENT' ? 'TENANT' : scope === 'TENANT' ? 'ORG' : 'Platform default';

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit?.();
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Failed to delete override.');
    } finally {
      setSubmitting(false);
    }
  };

  const diffRows = [
    { label: 'Block weight',          a: current.blockWeight,            b: fallback?.blockWeight },
    { label: 'Warn weight',           a: current.warnWeight,             b: fallback?.warnWeight },
    { label: 'Self-Auto threshold',   a: current.selfAutonomousThreshold,b: fallback?.selfAutonomousThreshold },
    { label: 'Supervised threshold',  a: current.supervisedThreshold,    b: fallback?.supervisedThreshold },
    { label: 'Restricted threshold',  a: current.restrictedThreshold,    b: fallback?.restrictedThreshold },
  ].filter((r) => r.a != null || r.b != null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      dismissOnBackdrop={!submitting}
      size="md"
      title={
        <span className="inline-flex items-center gap-2">
          <Trash2 size={18} className="text-v2-danger-strong" />
          Remove {scope.toLowerCase()} override
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="danger" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Removing…' : 'Remove override'}
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-v2-ink mb-3">
        Removing this override means {scope.toLowerCase()}-scoped trust scoring will inherit
        from <strong>{fallbackScope}</strong> going forward. Effective immediately on all
        subsequent evaluations.
      </p>

      {/* Inheritance preview — bookend the active config and the
          inherited one with an arrow so operators see WHAT changes. */}
      <div className="space-y-3 mb-3">
        <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <Pill tone="danger" size="sm">Removing</Pill>
            <span className="text-[12px] font-semibold text-v2-ink">{scope} override</span>
          </div>
          <DiffList rows={diffRows.map((r) => ({ label: r.label, value: r.a }))} />
        </div>

        <div className="flex justify-center">
          <ArrowDown size={18} className="text-v2-ink-muted" />
        </div>

        <div className="bg-v2-success-soft border border-v2-success/30 rounded-v2-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <Pill tone="success" size="sm">Falls back to</Pill>
            <span className="text-[12px] font-semibold text-v2-ink">{fallbackScope}</span>
          </div>
          {fallback ? (
            <DiffList rows={diffRows.map((r) => ({ label: r.label, value: r.b }))} />
          ) : (
            <p className="text-[12px] text-v2-ink-muted italic m-0">
              No higher-scope override configured — agent will use the platform-default
              trust scoring policy.
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 bg-v2-danger-soft border border-v2-danger/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-danger-strong">
          {error}
        </div>
      )}
    </Modal>
  );
}

function DiffList({ rows }) {
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11.5px]">
      {rows.map((r) => (
        <React.Fragment key={r.label}>
          <dt className="text-v2-ink-muted">{r.label}</dt>
          <dd className="text-v2-ink font-mono-v2 text-right m-0">{r.value ?? '—'}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export default DeleteScoringOverrideModal;
